import { useState, useEffect } from 'react';

// SHA-256 hashing function
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Canvas fingerprinting
function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#ff6b00';
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = '#ffffff';
    ctx.fillText('NexaScan Fingerprint', 2, 2);
    ctx.fillStyle = 'rgba(255, 107, 0, 0.5)';
    ctx.fillText('Canvas Test 🔒', 4, 20);
    
    return canvas.toDataURL();
  } catch (e) {
    return 'canvas-error';
  }
}

// WebGL fingerprinting
function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return 'no-webgl';
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: gl.getParameter(gl.VENDOR),
      renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown',
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
    };
  } catch (e) {
    return 'webgl-error';
  }
}

// Get IP and location information
async function getIPInfo() {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return {
      ip: data.ip,
      city: data.city,
      region: data.region,
      country: data.country_name,
      countryCode: data.country_code,
      timezone: data.timezone,
      isp: data.org,
      lat: data.latitude,
      lon: data.longitude,
    };
  } catch (e) {
    return null;
  }
}

// Get timezone offset
function getTimezoneOffset(timezone) {
  try {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (tzDate.getTime() - utcDate.getTime()) / 60000;
  } catch (e) {
    return 0;
  }
}

// Calculate Scamlytics Score
function calculateScamlyticsScore(data) {
  let score = 100;
  let risks = [];
  
  if (data.timezoneMatch === false) {
    score = 0;
    risks.push({ type: 'Timezone Mismatch Detected', severity: 'critical', impact: -100 });
  } else {
    risks.push({ type: 'Your Browser Fingerprints Are Consistent', severity: 'success', impact: 0 });
  }
  
  return {
    score,
    rating: score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Poor',
    color: score >= 80 ? 'green' : score >= 60 ? 'blue' : score >= 40 ? 'yellow' : 'red',
    risks
  };
}

// Comprehensive fingerprint collection
async function generateFingerprint(logCallback) {
  logCallback('> Initializing NexaScan ...');
  await new Promise(r => setTimeout(r, 1000));
  
  logCallback('> Collecting browser metadata...');
  await new Promise(r => setTimeout(r, 500));
  
  const browserData = {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      orientation: window.screen.orientation?.type || 'unknown',
      pixelRatio: window.devicePixelRatio,
    },
    navigator: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory || 'unknown',
      maxTouchPoints: navigator.maxTouchPoints,
      vendor: navigator.vendor,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint(),
    features: {
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      indexedDB: !!window.indexedDB,
      webWorker: !!window.Worker,
      webAssembly: !!window.WebAssembly,
      serviceWorker: 'serviceWorker' in navigator,
      webRTC: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    },
  };
  
  logCallback(`> Screen resolution: ${browserData.screen.width}x${browserData.screen.height}`);
  await new Promise(r => setTimeout(r, 700));
  

  logCallback('> Querying IP geolocation ...');
  await new Promise(r => setTimeout(r, 2000));
  
  const ipInfo = await getIPInfo();
  
  if (ipInfo) {
    logCallback(`> IP detected: ${ipInfo.ip}`);
    await new Promise(r => setTimeout(r, 3000));
    logCallback(`> Location: ${ipInfo.city}, ${ipInfo.country}`);
    await new Promise(r => setTimeout(r, 600));
  }
  
  logCallback('> Analyzing timezone consistency ...');
  await new Promise(r => setTimeout(r, 1000));
  
  const browserOffset = new Date().getTimezoneOffset() * -1;
  const ipOffset = ipInfo?.timezone ? getTimezoneOffset(ipInfo.timezone) : null;
  const timezoneMatch = ipOffset !== null && Math.abs(browserOffset - ipOffset) <= 60;
  
  logCallback(`> Browser timezone: ${browserData.timezone}`);
  await new Promise(r => setTimeout(r, 500));
  logCallback(`> IP timezone: ${ipInfo?.timezone || 'Unknown'}`);
  await new Promise(r => setTimeout(r, 1000));
  
  const browserLang = browserData.navigator.language.split('-')[1];
  const languageMismatch = ipInfo?.countryCode && browserLang && browserLang.toUpperCase() !== ipInfo.countryCode;
  
  const vpnIndicators = [];
  if (!timezoneMatch) vpnIndicators.push('Timezone mismatch');
  if (browserData.features.webRTC) vpnIndicators.push('WebRTC enabled');
  if (languageMismatch) vpnIndicators.push('Language/Location mismatch');
  
  logCallback('> Computing trust score ...');
  await new Promise(r => setTimeout(r, 5000));
  
  const result = {
    location: {
      detected: ipInfo ? `${ipInfo.city}, ${ipInfo.region}` : 'Unknown',
      country: ipInfo?.country || 'Unknown',
      timezone: browserData.timezone,
      ipTimezone: ipInfo?.timezone || 'Unknown',
      browserOffset: browserOffset,
      ipOffset: ipOffset,
    },
    ip: {
      address: ipInfo?.ip || 'Unknown',
      isp: ipInfo?.isp || 'Unknown',
    },
    timezoneMatch,
    vpnIndicators,
    languageMismatch,
    features: browserData.features,
    fingerprint: {
      screen: `${browserData.screen.width}x${browserData.screen.height}`,
      colorDepth: browserData.screen.colorDepth,
      platform: browserData.navigator.platform,
      hardwareCores: browserData.navigator.hardwareConcurrency,
      language: browserData.navigator.language,
    }
  };
  
  result.scamlytics = calculateScamlyticsScore(result);
  
  logCallback('> Scan complete!');
  await new Promise(r => setTimeout(r, 400));
  
  return result;
}

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [terminalLogs, setTerminalLogs] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchFingerprint = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      setError(null);
      setTerminalLogs([]);
      setProgress(0);
      
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 400);
      
      try {
        const fingerprint = await generateFingerprint((log) => {
          if (isMounted) {
            setTerminalLogs(prev => [...prev, log]);
          }
        });
        if (isMounted) {
          setData(fingerprint);
          setProgress(100);
        }
        clearInterval(progressInterval);
      } catch (err) {
        if (isMounted) {
          setError(err);
        }
        clearInterval(progressInterval);
        console.error('Fingerprint generation error:', err);
      } finally {
        if (isMounted) {
          setTimeout(() => setIsLoading(false), 300);
        }
      }
    };

    fetchFingerprint();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreBgGradient = (score) => {
    if (score >= 80) return 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)';
    if (score >= 60) return 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.1) 100%)';
    if (score >= 40) return 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)';
    return 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #1a1a1a 0%, #0d0d0d 50%, #1a1a1a 100%)',
      color: '#ffffff',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ position: 'relative', marginBottom: '4rem', overflow: 'hidden' }}>
          {/* Animated Background */}
          <div style={{ position: 'absolute', inset: 0, zIndex: -10 }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: '25%',
              width: '384px',
              height: '384px',
              background: 'radial-gradient(circle, rgba(255, 107, 0, 0.1) 0%, transparent 70%)',
              filter: 'blur(64px)',
              animation: 'pulse 3s ease-in-out infinite',
            }}></div>
            <div style={{
              position: 'absolute',
              top: 0,
              right: '25%',
              width: '384px',
              height: '384px',
              background: 'radial-gradient(circle, rgba(255, 149, 0, 0.08) 0%, transparent 70%)',
              filter: 'blur(64px)',
              animation: 'pulse 3s ease-in-out infinite',
              animationDelay: '1s',
            }}></div>
          </div>
          
          <nav style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 50,
            background: 'rgba(26, 26, 26, 0.6)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255, 107, 0, 0.2)',
            padding: '0.75rem 3rem',
          }}>
            <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                  boxShadow: '0 4px 20px rgba(255, 107, 0, 0.3)',
                }}>
                  <svg style={{ width: '24px', height: '24px', color: '#ffffff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                </div>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                  <span style={{ color: '#ff6b00' }}>Nexa</span>
                  <span style={{ color: '#ff9500' }}>Scan</span>
                </h1>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <a href="#proxies" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 10px rgba(255, 107, 0, 0.2)',
                  transition: 'all 0.2s',
                }}>
                  Get Proxies
                </a>

                <a href="#best-practices" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#2d2d2d',
                  border: '1px solid rgba(255, 107, 0, 0.3)',
                  borderRadius: '8px',
                  fontWeight: 600,
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                }}>
                  Best Practices
                </a>
              </div>
            </div>
          </nav>
        </div>

        {/* Terminal Loading */}
        {isLoading && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              background: '#0d0d0d',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(255, 107, 0, 0.3)',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              minHeight: '200px',
              maxHeight: '300px',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}>
                {terminalLogs.slice(-8).map((log, idx) => (
                  <div key={idx} style={{
                    color: log.includes('complete') ? '#10b981' : '#ff9500',
                    animation: 'fadeIn 0.3s ease-in',
                  }}>
                    {log}
                  </div>
                ))}
                <div style={{
                  color: '#ff6b00',
                  animation: 'blink 1s infinite',
                  display: 'inline-block',
                }}>▊</div>
              </div>
            </div>
            
            <div style={{
              background: 'rgba(45, 45, 45, 0.5)',
              borderRadius: '9999px',
              height: '8px',
              overflow: 'hidden',
              marginTop: '1rem',
            }}>
              <div style={{
                background: 'linear-gradient(90deg, #ff6b00 0%, #ff9500 100%)',
                height: '100%',
                width: `${progress}%`,
                transition: 'width 0.3s',
              }} />
            </div>
            <p style={{ textAlign: 'center', color: '#888888', marginTop: '0.5rem', fontSize: '0.875rem' }}>
              Analyzing your browser fingerprint... {progress}%
            </p>
          </div>
        )}

        {/* Main Content */}
        {!isLoading && data && (
          <>
            {/* Scamlytics Score */}
            <div style={{
              background: getScoreBgGradient(data.scamlytics.score),
              borderRadius: '24px',
              padding: '2rem',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>Scamlytics Trust Score</h2>
                  <p style={{ color: '#888888', fontSize: '1.125rem' }}>Overall fingerprint security rating</p>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
                  <div style={{ position: 'relative', width: '160px', height: '160px' }}>
                    <svg style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                      <circle cx="80" cy="80" r="70" stroke="rgba(255, 255, 255, 0.1)" strokeWidth="12" fill="none" />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke={getScoreColor(data.scamlytics.score)}
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 70}`}
                        strokeDashoffset={`${2 * Math.PI * 70 * (1 - data.scamlytics.score / 100)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '3rem', fontWeight: 700, color: getScoreColor(data.scamlytics.score) }}>
                        {data.scamlytics.score}
                      </span>
                      <span style={{ color: '#888888', fontSize: '0.875rem' }}>{data.scamlytics.rating}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Factors */}
              {data.scamlytics.risks.length > 0 && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255, 107, 0, 0.2)' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.75rem' }}>Analysis Result:</h3>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {data.scamlytics.risks.map((risk, idx) => (
                      <div key={idx} style={{
                        borderRadius: '8px',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        background: risk.severity === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${risk.severity === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: risk.severity === 'success' ? '#10b981' : '#ef4444',
                        }} />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 500 }}>{risk.type}</span>
                          {risk.impact !== 0 && (
                            <span style={{ fontSize: '0.875rem', color: '#888888', marginLeft: '0.5rem' }}>
                              ({risk.impact} points)
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* IP & Location Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              <div style={{
                background: 'rgba(45, 45, 45, 0.5)',
                backdropFilter: 'blur(8px)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: '1px solid rgba(255, 107, 0, 0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.2) 0%, rgba(255, 149, 0, 0.2) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <svg style={{ width: '28px', height: '28px', color: '#ff9500' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>IP Address</h3>
                    <p style={{ color: '#888888', fontSize: '0.875rem' }}>Network Identity</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888888' }}>Address:</span>
                    <span style={{ fontFamily: 'monospace', color: '#ff9500' }}>{data.ip.address}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888888' }}>ISP:</span>
                    <span style={{ fontSize: '0.875rem', textAlign: 'right' }}>{data.ip.isp}</span>
                  </div>
                </div>
              </div>

              <div style={{
                background: 'rgba(45, 45, 45, 0.5)',
                backdropFilter: 'blur(8px)',
                borderRadius: '16px',
                padding: '1.5rem',
                border: '1px solid rgba(255, 107, 0, 0.2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.2) 0%, rgba(255, 149, 0, 0.2) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <svg style={{ width: '28px', height: '28px', color: '#ff9500' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Location</h3>
                    <p style={{ color: '#888888', fontSize: '0.875rem' }}>Geographic Position</p>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888888' }}>Detected:</span>
                    <span style={{ fontWeight: 500 }}>{data.location.detected}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#888888' }}>Country:</span>
                    <span style={{ fontWeight: 500 }}>{data.location.country}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timezone Analysis */}
            <div style={{
              background: 'rgba(45, 45, 45, 0.5)',
              backdropFilter: 'blur(8px)',
              borderRadius: '16px',
              padding: '1.5rem',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              marginBottom: '2rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.2) 0%, rgba(255, 149, 0, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg style={{ width: '28px', height: '28px', color: '#ff9500' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Timezone Analysis</h3>
                  <p style={{ color: '#888888' }}>Browser vs IP location comparison</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                <div style={{ background: 'rgba(26, 26, 26, 0.5)', borderRadius: '12px', padding: '1.25rem' }}>
                  <h4 style={{ fontWeight: 600, color: '#ff9500', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Browser Timezone
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#888888', fontSize: '0.875rem' }}>Timezone:</span>
                      <p style={{ fontFamily: 'monospace', fontSize: '1.125rem' }}>{data.location.timezone}</p>
                    </div>
                    <div>
                      <span style={{ color: '#888888', fontSize: '0.875rem' }}>UTC Offset:</span>
                      <p style={{ fontFamily: 'monospace', fontSize: '1.125rem' }}>
                        {data.location.browserOffset >= 0 ? '+' : ''}{(data.location.browserOffset / 60).toFixed(1)} hours
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ background: 'rgba(26, 26, 26, 0.5)', borderRadius: '12px', padding: '1.25rem' }}>
                  <h4 style={{ fontWeight: 600, color: '#ff6b00', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    IP Location Timezone
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#888888', fontSize: '0.875rem' }}>Timezone:</span>
                      <p style={{ fontFamily: 'monospace', fontSize: '1.125rem' }}>{data.location.ipTimezone}</p>
                    </div>
                    <div>
                      <span style={{ color: '#888888', fontSize: '0.875rem' }}>UTC Offset:</span>
                      <p style={{ fontFamily: 'monospace', fontSize: '1.125rem' }}>
                        {data.location.ipOffset >= 0 ? '+' : ''}{(data.location.ipOffset / 60).toFixed(1)} hours
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                borderRadius: '8px',
                background: data.timezoneMatch ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${data.timezoneMatch ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {data.timezoneMatch ? (
                    <svg style={{ width: '24px', height: '24px', color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg style={{ width: '24px', height: '24px', color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <div>
                    <p style={{ fontWeight: 600, color: data.timezoneMatch ? '#10b981' : '#ef4444' }}>
                      {data.timezoneMatch ? 'Timezone Match: Consistent' : 'Timezone Mismatch Detected'}
                    </p>
                    <p style={{ color: '#888888', fontSize: '0.875rem' }}>
                      {data.timezoneMatch 
                        ? 'Your browser timezone matches your IP location timezone'
                        : 'Your browser timezone does not match your IP location - possible VPN/proxy usage'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Automation Detection */}
            <div style={{
              background: 'rgba(45, 45, 45, 0.5)',
              borderRadius: '12px',
              padding: '1rem',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              marginBottom: '3rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <svg style={{ width: '20px', height: '20px', color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span style={{ color: '#888888' }}>No automation framework detected</span>
              </div>
            </div>

            {/* Best Practices Section */}
            <div id="best-practices" style={{
              background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(26, 26, 26, 0.8) 100%)',
              backdropFilter: 'blur(8px)',
              borderRadius: '24px',
              padding: '2rem',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              marginBottom: '2rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.2) 0%, rgba(255, 149, 0, 0.2) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg style={{ width: '32px', height: '32px', color: '#ff9500' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700 }}>Proxy Best Practices</h2>
                  <p style={{ color: '#888888' }}>Essential tips for maintaining consistent fingerprints</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                {[
                  { num: 1, color: '#ff6b00', title: 'Match Timezone to Proxy Location', desc: 'Always configure your browser\'s timezone to match your proxy\'s geographic location. This is the #1 factor in avoiding detection.' },
                  { num: 2, color: '#ff9500', title: 'Use Residential Proxies', desc: 'Residential proxies are harder to detect than datacenter proxies. They appear as regular home connections and blend in naturally.' },
                  { num: 3, color: '#ff6b00', title: 'Consistent Browser Fingerprint', desc: 'Keep your browser fingerprint consistent across sessions. Use browser profiles or anti-detect browsers to maintain the same settings.' },
                  { num: 4, color: '#ff9500', title: 'Rotate Proxies Wisely', desc: 'Don\'t rotate too frequently. Stick with one proxy per session and rotate between sessions to maintain trust scores.' },
                  { num: 5, color: '#ff6b00', title: 'Disable WebRTC Leaks', desc: 'WebRTC can leak your real IP address even when using a proxy. Always disable it or use browser extensions to prevent leaks.' },
                  { num: 6, color: '#ff9500', title: 'Match Language Settings', desc: 'Set your browser language to match the proxy location\'s country. Mismatched languages can flag your connection as suspicious.' },
                ].map((practice, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(26, 26, 26, 0.5)',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    border: '1px solid rgba(255, 107, 0, 0.15)',
                    transition: 'all 0.3s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: `rgba(${practice.color === '#ff6b00' ? '255, 107, 0' : '255, 149, 0'}, 0.2)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <span style={{ color: practice.color, fontWeight: 700 }}>{practice.num}</span>
                      </div>
                      <div>
                        <h3 style={{ fontWeight: 600, fontSize: '1.125rem', marginBottom: '0.5rem', color: practice.color }}>
                          {practice.title}
                        </h3>
                        <p style={{ color: '#888888', fontSize: '0.875rem', lineHeight: '1.5' }}>
                          {practice.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Premium Proxies CTA */}
            <div id="proxies" style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 149, 0, 0.1) 100%)',
              backdropFilter: 'blur(8px)',
              borderRadius: '24px',
              padding: '2rem',
              border: '1px solid rgba(255, 107, 0, 0.3)',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                  marginBottom: '1.5rem',
                  boxShadow: '0 10px 40px rgba(255, 107, 0, 0.3)',
                }}>
                  <svg style={{ width: '36px', height: '36px', color: '#ffffff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                
                <h2 style={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Need Premium Proxies?
                </h2>
                <p style={{ color: '#888888', fontSize: '1.125rem', marginBottom: '1.5rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
                  Get access to high-quality residential proxies with perfect timezone matching, unlimited bandwidth, and 99.9% uptime guarantee.
                </p>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  {['100% Timezone Match', 'Residential IPs', '24/7 Support'].map((feature, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#888888' }}>
                      <svg style={{ width: '20px', height: '20px', color: '#10b981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <a 
                  href="https://nexaproxy.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1.25rem 2.5rem',
                    background: 'linear-gradient(135deg, #ff6b00 0%, #ff9500 100%)',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    color: '#ffffff',
                    textDecoration: 'none',
                    boxShadow: '0 10px 40px rgba(255, 107, 0, 0.3)',
                    transition: 'all 0.3s',
                  }}
                >
                  <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Browse Premium Proxies
                  <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
                
                <p style={{ color: '#888888', fontSize: '0.875rem', marginTop: '1rem' }}>
                  Starting from $1.34
                </p>
              </div>
            </div>
          </>
        )}

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '1.5rem',
            textAlign: 'center',
          }}>
            <svg style={{ width: '48px', height: '48px', color: '#ef4444', margin: '0 auto 0.75rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p style={{ color: '#ef4444', fontWeight: 600 }}>Error generating fingerprint</p>
            <p style={{ color: '#888888', fontSize: '0.875rem', marginTop: '0.5rem' }}>Please refresh the page to try again</p>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}